import * as React from "react";
import {
  AllCaps,
  Container,
  HeadingGroup,
  Link,
  PageHeader,
  SiteContainer,
  Stack,
  Text,
} from "atoms";
import { useSearchParams } from "react-router-dom";
import { useCollective } from "features/collectives";
import { Head } from "features/misc";
import { useProfile } from "features/profile";
import ArtistApplicationForm from "./ArtistApplicationForm";

const REFERRAL_ID_PARAM = "code";
const REFERRAL_ID_KEY = "HUG_artist_referral_id";
const APPLICATION_BYPASS_PARAM = "collective";

function CreateArtistApplication() {
  // Get referrer's ID from search params or local storage
  const [searchParams, setSearchParams] = useSearchParams();
  const urlId = searchParams.get(REFERRAL_ID_PARAM);
  const storedId = window.localStorage.getItem(REFERRAL_ID_KEY);
  const referralId = urlId ?? storedId ?? undefined;

  const bypassId = searchParams.get(APPLICATION_BYPASS_PARAM) ?? undefined;

  React.useEffect(() => {
    if (urlId) {
      // Store the referrer's ID in case the invited user leaves this page and returns
      window.localStorage.setItem(REFERRAL_ID_KEY, urlId);
      // Remove the ID from the URL to clean it up
      searchParams.delete(REFERRAL_ID_PARAM);
      setSearchParams(searchParams);
    }
  }, [urlId, searchParams, setSearchParams]);

  return (
    <>
      <Head />

      <PageHeader brush="1" bg="black" overlap>
        <Container width="sm">
          <Stack gap="sm">
            <HeadingGroup>
              <small role="heading" aria-level={1}>
                My Artist Application
              </small>

              {/* Show the Collective Invite headline even if `referralId` exists. */}
              {bypassId && <CollectiveText id={bypassId} />}

              {referralId && !bypassId && <RefererText id={referralId} />}

              {!referralId && !bypassId && (
                <Text size="h2" bold font="display" textTransform="uppercase">
                  Get Your Artist&nbsp;Profile
                </Text>
              )}
            </HeadingGroup>

            <Container width="xs">
              <Text size="xs">
                A <AllCaps>Hug</AllCaps> Artist&nbsp;Profile is the ultimate
                tool to grow your audience and connect with artists and art
                lovers like never before.{" "}
                <Link to="/artist-profile-benefits">
                  Learn&nbsp;more&nbsp;about&nbsp;the&nbsp;benefits
                </Link>
                &#8288;.
              </Text>
            </Container>
          </Stack>
        </Container>
      </PageHeader>

      <SiteContainer width="xs">
        <ArtistApplicationForm
          referralId={referralId}
          applicationBypassId={bypassId}
        />
      </SiteContainer>
    </>
  );
}

function RefererText({ id }: { id: string }) {
  const { data } = useProfile(id);

  return (
    <Text size="xxl" bold font="display" textTransform="uppercase">
      {data?.name ? (
        <>
          {data.name} invited&nbsp;you <br />
        </>
      ) : (
        <>
          You&apos;ve been invited <br />
        </>
      )}
      to&nbsp;apply for a <AllCaps>Hug</AllCaps>
      &nbsp;Artist&nbsp;Profile!
    </Text>
  );
}

function CollectiveText({ id }: { id: string }) {
  const { data } = useCollective(id.split("~")[0]);
  if (!data) return null;

  return (
    <Text size="xxl" bold font="display" textTransform="uppercase">
      {data.name} invited you to join their collective on&nbsp;
      <AllCaps>Hug</AllCaps>
    </Text>
  );
}

export { APPLICATION_BYPASS_PARAM };
export default CreateArtistApplication;
